import { sendTrackEvent } from "@sinch/bus";
import { EVENT_NAME } from "../analytics/createRudderstackEvent";
import { matchPath } from "../components/Router/utils";
import { getMessageMediaApiUrlMiddleware } from "../config/config";
import {
  clearStorage,
  getLocalStorage,
  getStorageData,
  isStringOrNull,
  listenToLocalStorageChange,
  setLocalStorage,
  REFRESH_TOKEN,
} from "../helpers/localStorage";
import { loginHubPaths } from "../navigationConfig/mmNavigationConfig";
import { APP_NAME } from "../renderShell";
import type { AuthStrategy, Token } from "./types";

export type MMUserSessionType = {
  accessToken: string;
  refreshToken: string;
  expireIn: number;
  createdAt: number;
};

type MMUserRefreshResponse = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
};

// Placeholder which we will replace in all calls to fetch and XMLHttpRequest.
// It must be url-safe and also quite unique. So, quoting RFC3986
// (https://www.ietf.org/rfc/rfc3986.txt)
//
// 2.3.  Unreserved Characters
//
//   Characters that are allowed in a URI but do not have a reserved
//   purpose are called unreserved.  These include uppercase and lowercase
//   letters, decimal digits, hyphen, period, underscore, and tilde.
//
//      unreserved  = ALPHA / DIGIT / "-" / "." / "_" / "~"
//
// Leaves us with the characters we can use safely.
//
// We also exclude "." to be regex safe.
//
const symbolicToken = "___---TOKEN-PLACEHOLDER---___";

let userSession: MMUserSessionType | undefined;

export const tokenSecondsLeft = (session: MMUserSessionType) =>
  session.expireIn - (Math.floor(Date.now() / 1000) - session.createdAt);

const checkIfSessionValid = (session: MMUserSessionType) =>
  Boolean(
    session &&
      session.accessToken &&
      session.createdAt &&
      session.expireIn &&
      tokenSecondsLeft(session) - 10 > 0
  );

const { fetch: originalFetch } = window;

const decorateUserSession = (
  authData: MMUserRefreshResponse
): MMUserSessionType => {
  const refreshToken =
    authData.refresh_token || userSession?.refreshToken || getSavedToken();
  if (!refreshToken) {
    throw new Error("Cannot store session: refreshToken not found.");
  }
  return {
    accessToken: authData.access_token,
    refreshToken,
    expireIn: authData.expires_in,
    createdAt: Math.floor(Date.now() / 1000),
  };
};

const storeSession = (response: MMUserRefreshResponse) => {
  userSession = decorateUserSession(response);

  return userSession;
};

const getSavedToken = () =>
  getLocalStorage(REFRESH_TOKEN, isStringOrNull, null);

const handleAuthResponse = async (response: Response) => {
  const authData = (await response.json()) as MMUserRefreshResponse;
  storeSession(authData);
  return authData;
};

/**
 * Locking mechanism to make sure multiple instances of the useMMToken hook
 * refreshing the token at once will use only one Promise
 */
let accessTokenUpdatePromise: Promise<Partial<MMUserSessionType>> | undefined;

const fetchNewToken = (refreshToken?: string) => {
  // If already fetching, return current promise
  if (accessTokenUpdatePromise) {
    return accessTokenUpdatePromise;
  }
  const refresh_token = refreshToken || getSavedToken();

  if (!refresh_token) {
    console.warn("No Refresh token found! Have you logged in?");
    return;
  }

  const body = JSON.stringify({
    grant_type: "refresh_token",
    refresh_token,
  });
  accessTokenUpdatePromise = originalFetch(
    `${getMessageMediaApiUrlMiddleware()}/iam/v1/jwt/auth`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body,
    }
  ).then(async (res) => {
    if (!res.ok) {
      throw new Error(`Couldn't retrieve refresh token ${await res.text()}`);
    }
    await handleAuthResponse(res);
    accessTokenUpdatePromise = undefined;
    return { ...userSession };
  });
  return accessTokenUpdatePromise;
};

const checkIsLoginFlow = () => {
  const urlParams = new URLSearchParams(location.search);

  return (
    loginHubPaths.includes(location.pathname) &&
    (urlParams.get("token") || urlParams.get("tokenId"))
  );
};

export const getSession: AuthStrategy["getSession"] = async (): Promise<
  Partial<MMUserSessionType> | undefined
> => {
  // Is checking for the path necessary or maybe we could just skip it and
  // handle any urls that has a token in the param?
  if (checkIsLoginFlow()) return;

  if (userSession && checkIfSessionValid(userSession)) {
    return userSession;
  }

  return fetchNewToken();
};

export const logout: AuthStrategy["logout"] = async () => {
  sendTrackEvent({
    name: EVENT_NAME.USER_LOGOUT,
    appName: APP_NAME,
    purposes: ["Analytical"],
  });
  const { token } = getToken() || { token: "" };
  const redirectPath = "/login";
  const redirectUrl = getStorageData("loginFrom") || redirectPath;
  try {
    await originalFetch(
      `${getMessageMediaApiUrlMiddleware()}/iam/v1/jwt/auth/revoke`,
      {
        method: "POST",
        headers: {
          "x-app-version": "mmgp-frontend/undefined-x",
          "Content-Type": "application/json",
          token: "refresh_token",
          requestToken: token,
        },
        credentials: "include",
      }
    );
    window.location.replace(redirectUrl);
  } catch (e) {
    console.error(e);
  } finally {
    clearStorage();
    sessionStorage.clear();
    window.location.replace(redirectUrl);
  }
};

let isPatchingRefreshToken = false;
export const patchRefreshToken: AuthStrategy["patchRefreshToken"] = async (
  { accountId, onPatchSuccess, redirectUrl = "/" },
  options
) => {
  if (isPatchingRefreshToken) {
    return;
  }
  isPatchingRefreshToken = true;
  const refresh_token = getSavedToken();

  if (!refresh_token) {
    console.error("No Refresh token found! Have you logged in?");
    return;
  }

  const body = JSON.stringify({
    grant_type: "refresh_token",
    refresh_token,
    ...options,
    ...(accountId ? { accountId } : {}),
  });

  const response = await originalFetch(
    `${getMessageMediaApiUrlMiddleware()}/iam/v1/jwt/auth`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body,
    }
  );

  if (!response.ok) {
    isPatchingRefreshToken = false;
    throw new Error(`Couldn't retrieve refresh token ${await response.text()}`);
  }

  const newLoginData = await handleAuthResponse(response);
  // Only place where the shell can write the refresh token
  setLocalStorage(REFRESH_TOKEN, newLoginData.refresh_token);
  onPatchSuccess?.();

  window.location.assign(redirectUrl);
};

export const getToken: AuthStrategy["getToken"] = () =>
  userSession?.accessToken
    ? {
        token: symbolicToken,
        parsedToken: {}, // TODO: parse token
      }
    : null;

export const getTokenP: AuthStrategy["getTokenP"] = async () => {
  const token = await getSession().then(() => getToken());
  if (!token) {
    throw new Error("Couldn't retrieve token");
  }
  return token;
};

// This is the very first thing that is done by the shell.
export const initMessageMediaAuth: AuthStrategy["init"] = () => {
  const isAuthScreen = () =>
    loginHubPaths.some((authPath) => matchPath(authPath, location.pathname));

  getSession().then((session) => {
    if (!session && !isAuthScreen()) {
      history.pushState(
        "",
        "",
        `/login?redirect=${encodeURIComponent(location.pathname)}`
      );
    }
  });
};

const parseToken = (token: string): string => {
  try {
    return JSON.parse(token);
  } catch (error) {
    console.error("Failed to parse refresh token", error);
    return token;
  }
};

export const onTokenUpdate: AuthStrategy["onTokenUpdate"] = (
  callback: (token: Token | null) => void
) =>
  listenToLocalStorageChange(REFRESH_TOKEN, () => {
    const newRefreshToken = localStorage.getItem(REFRESH_TOKEN);
    if (newRefreshToken) {
      // consider that if there wasn't a token before and now we got one it's a login
      if (!userSession?.refreshToken) {
        sendTrackEvent({
          name: EVENT_NAME.USER_LOGIN,
          appName: APP_NAME,
          purposes: ["Analytical"],
        });
      }
      fetchNewToken(parseToken(newRefreshToken))?.then(() =>
        callback(getToken())
      );
    } else {
      userSession = undefined;
    }
  });

// TODO - move to @mfe/types
const urlSymbol = Symbol("url");
const tokenSetSymbol = Symbol("tokenSet");

declare global {
  interface XMLHttpRequest {
    [urlSymbol]?: string | URL;
    [tokenSetSymbol]?: boolean;
  }
}

// Monkey patch code where the token is used to replace fake token with real.
// These are the things patched:
//
// * fetch
// * XMLHttpRequest
// * window.open
// * WebSocket
//
export const patchRequests: AuthStrategy["patchRequests"] = () => {
  const matchUrlForToken = (req: Request | URL | string) => {
    const apiHostname = new URL(getMessageMediaApiUrlMiddleware()).hostname;
    const hostname = (
      req instanceof URL ? req : new URL(req instanceof Request ? req.url : req)
    ).hostname;

    return (
      hostname.endsWith(apiHostname) ||
      hostname.endsWith(".messagemedia.com") ||
      hostname.endsWith(".sinch.com") ||
      hostname.endsWith(".messengerpeople.com") ||
      hostname.endsWith(".chatlayer.ai") ||
      hostname.endsWith(".mmd.zone") ||
      hostname.endsWith(".wbcsrv.com") ||
      hostname == "localhost"
    );
  };

  const placeholderRegEx = new RegExp(symbolicToken, "g");
  const replaceToken = (
    value: string,
    session:
      | {
          accessToken?: string | undefined;
        }
      | undefined
  ) =>
    value?.replace?.(placeholderRegEx, session?.accessToken ?? "N/A") ?? value;

  //////////////////////////////////////////////////////////////////////////////
  // Patch fetch
  window.fetch = async (input, init) => {
    const patchedRequest = new Request(input, init);

    if (matchUrlForToken(patchedRequest)) {
      const session = await getSession();
      patchedRequest.headers.forEach((value, name) => {
        patchedRequest.headers.set(name, replaceToken(value, session));
      });
    }

    return originalFetch(patchedRequest);
  };

  //////////////////////////////////////////////////////////////////////////////
  // Patch XMLHttpRequest
  const { setRequestHeader } = XMLHttpRequest.prototype;

  XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
    if (name.toLowerCase() === "authorization") {
      this[tokenSetSymbol] = true;
      return setRequestHeader.call(
        this,
        name,
        replaceToken(value, userSession)
      );
    }
    return setRequestHeader.call(this, name, value);
  };

  //////////////////////////////////////////////////////////////////////////////
  // Patch WebSocket
  const { send } = WebSocket.prototype;
  WebSocket.prototype.send = function (data) {
    return send.call(
      this,
      // Can't be async...
      // TODO: figure out how to handle expired tokens here.
      typeof data == "string"
        ? replaceToken(data, { accessToken: userSession?.accessToken })
        : data
    );
  };

  // reset to original
  return () => {
    window.fetch = originalFetch;
    XMLHttpRequest.prototype.setRequestHeader = setRequestHeader;
    delete XMLHttpRequest.prototype.useShellToken;
    WebSocket.prototype.send = send;
  };
};
